import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import {
  Organization,
  Plan,
  OrganizationUser,
  OrganizationUsageStats,
  Subscription,
  BillingCycle,
  DashboardGoals,
  WhatsAppAccount,
  isPaymentRequiredError,
  isPlanLimitError,
  getErrorMessage
} from '../../types';
import { AISettingsTab } from '../../components/AISettingsTab';
import { KanbanSettingsTab } from '../../components/KanbanSettingsTab/KanbanSettingsTab';
import './Settings.css';

export type TabType = 'general' | 'users' | 'whatsapp' | 'plan' | 'dashboard' | 'ai' | 'kanban';
interface SettingsProps {
  embedded?: boolean;
  onBackToDashboard?: () => void;
  onOpenStatsDashboard?: () => void;
  initialTab?: TabType;
}

const TIMEZONE_OPTIONS = [
  'America/Sao_Paulo',
  'America/Manaus',
  'America/Belem',
  'America/Fortaleza',
  'America/Recife',
  'America/Bahia',
  'America/Cuiaba',
  'America/Porto_Velho',
  'America/Rio_Branco',
  'UTC',
];

export const Settings: React.FC<SettingsProps> = ({
  embedded = false,
  onBackToDashboard,
  onOpenStatsDashboard,
  initialTab,
}) => {
  const navigate = useNavigate();
  const { usuario, isOwner, isAdmin, isGerente, organizationId, refreshUsuario } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>(initialTab ?? 'general');
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [users, setUsers] = useState<OrganizationUser[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [usage, setUsage] = useState<OrganizationUsageStats | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [whatsappAccounts, setWhatsappAccounts] = useState<WhatsAppAccount[]>([]);
  const [dashboardGoals, setDashboardGoals] = useState<DashboardGoals | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');

  // Modal states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showEditOrgModal, setShowEditOrgModal] = useState(false);
  const [showAddWhatsAppModal, setShowAddWhatsAppModal] = useState(false);
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [showAssignUsersModal, setShowAssignUsersModal] = useState(false);
  const [showUserWhatsAppModal, setShowUserWhatsAppModal] = useState(false);
  const [userWhatsAppEdit, setUserWhatsAppEdit] = useState<OrganizationUser | null>(null);
  const [editWaPrincipal, setEditWaPrincipal] = useState('');
  const [editWaCampanha, setEditWaCampanha] = useState('');

  // Form states
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'gerente' | 'atendente'>('atendente');
  const [editOrgName, setEditOrgName] = useState('');
  const [editOrgTimezone, setEditOrgTimezone] = useState('America/Sao_Paulo');
  const [editUnsubEnabled, setEditUnsubEnabled] = useState(true);
  const [editUnsubBaseUrl, setEditUnsubBaseUrl] = useState('');
  const [editUnsubFooterTemplate, setEditUnsubFooterTemplate] = useState(
    'Se não quiser mais receber mensagens como esta, clique no link abaixo:\n{unsubscribeUrl}'
  );
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [newWhatsAppName, setNewWhatsAppName] = useState('');
  const [newWhatsAppPhone, setNewWhatsAppPhone] = useState('');
  const [selectedWhatsAppAccount, setSelectedWhatsAppAccount] = useState<WhatsAppAccount | null>(null);
  const [qrCodeData, setQrCodeData] = useState<string | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [campaignRemarketingMonths, setCampaignRemarketingMonths] = useState('');
  const [campaignRemarketingSaving, setCampaignRemarketingSaving] = useState(false);

  // Loading states for actions
  const [actionLoading, setActionLoading] = useState(false);

  const loadData = useCallback(async () => {
    if (!organizationId) {
      setError('Organização não encontrada');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Load all data in parallel
      const [orgData, usersData, plansData, subscriptionData, whatsappData] = await Promise.all([
        apiService.getOrganization(organizationId),
        apiService.getOrganizationUsers(organizationId),
        apiService.getPlans(),
        apiService.getSubscription().catch(() => null),
        apiService.getWhatsAppAccounts().catch(() => []),
      ]);

      setOrganization(orgData);
      setCampaignRemarketingMonths(
        orgData.defaultRemarketingMeses != null && orgData.defaultRemarketingMeses >= 1
          ? String(orgData.defaultRemarketingMeses)
          : '12'
      );
      setUsers(usersData.users || []);
      setPlans(plansData);
      setSubscription(subscriptionData);
      setWhatsappAccounts(whatsappData || []);
      setEditOrgName(orgData.name);
      setEditOrgTimezone(orgData.timezone || 'America/Sao_Paulo');
      setEditUnsubEnabled(orgData.unsubscribeSettings?.enabled ?? true);
      setEditUnsubBaseUrl(orgData.unsubscribeSettings?.baseUrl || '');
      setEditUnsubFooterTemplate(
        orgData.unsubscribeSettings?.footerTemplate ||
          'Se não quiser mais receber mensagens como esta, clique no link abaixo:\n{unsubscribeUrl}'
      );

      const goals = await apiService.getDashboardGoals(organizationId).catch(() => null);
      setDashboardGoals(goals);

      // Calculate usage from loaded data
      // Use effectiveLimits from organization (includes feature flags) or fallback to plan
      const currentPlan = plansData.find(p => p.planId === orgData.planId);
      const effectiveLimits = (orgData as any).effectiveLimits;
      
      setUsage({
        usersCount: usersData.users?.length || 0,
        maxUsers: effectiveLimits?.maxUsers ?? currentPlan?.maxUsers ?? 0,
        leadsThisMonth: orgData.leadsThisMonth || 0,
        maxLeadsPerMonth: effectiveLimits?.maxLeadsPerMonth ?? currentPlan?.maxLeadsPerMonth ?? 0,
        whatsappAccounts: whatsappData?.length || 0,
        maxWhatsAppAccounts: effectiveLimits?.maxWhatsAppAccounts ?? currentPlan?.maxWhatsAppAccounts ?? 0,
      });

    } catch (err: any) {
      console.error('Erro ao carregar dados:', err);
      setError(err.message || 'Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSaveCampaignRemarketing = async () => {
    if (!organizationId) return;
    const trimmed = campaignRemarketingMonths.trim();
    const value = trimmed === '' ? null : Number(trimmed);
    if (value !== null && (!Number.isInteger(value) || value < 1 || value > 120)) {
      alert('Informe um inteiro entre 1 e 120 meses, ou deixe em branco para desativar.');
      return;
    }
    setCampaignRemarketingSaving(true);
    try {
      await apiService.updateCampaignRemarketingDefaults(organizationId, value);
      setOrganization((prev) =>
        prev ? { ...prev, defaultRemarketingMeses: value === null ? null : value } : prev
      );
      alert('Remarketing de campanhas atualizado.');
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Não foi possível salvar.');
    } finally {
      setCampaignRemarketingSaving(false);
    }
  };

  const currentPlan = plans.find(p => p.planId === organization?.planId);

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organizationId) return;

    try {
      setActionLoading(true);
      const response = await apiService.inviteUser(organizationId, {
        email: inviteEmail,
        role: inviteRole,
      });

      // Add pending user to list
      const newUser: OrganizationUser = {
        usuarioId: response.invite.inviteId,
        inviteId: response.invite.inviteId,
        nome: inviteEmail.split('@')[0],
        email: inviteEmail,
        role: inviteRole,
        ativo: true,
        inviteStatus: 'pending',
        createdAt: response.invite.createdAt,
        updatedAt: response.invite.createdAt,
      };

      setUsers([...users, newUser]);
      setInviteEmail('');
      setInviteRole('atendente');
      setShowInviteModal(false);
    } catch (err: any) {
      console.error('Erro ao convidar usuário:', err);

      // Handle payment required error
      if (isPaymentRequiredError(err)) {
        setShowInviteModal(false);
        if (window.confirm(getErrorMessage(err) + '\n\nDeseja fazer upgrade agora?')) {
          setActiveTab('plan');
        }
        return;
      }

      // Handle plan limit error
      if (isPlanLimitError(err)) {
        setShowInviteModal(false);
        if (window.confirm(getErrorMessage(err) + '\n\nDeseja fazer upgrade do plano?')) {
          setActiveTab('plan');
        }
        return;
      }

      alert(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!organizationId) return;
    if (!window.confirm('Tem certeza que deseja remover este usuário?')) return;

    try {
      setActionLoading(true);
      await apiService.removeUserFromOrganization(organizationId, userId);
      setUsers(users.filter(u => u.usuarioId !== userId));
    } catch (err: any) {
      console.error('Erro ao remover usuário:', err);
      alert(err.response?.data?.message || 'Erro ao remover usuário');
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeRole = async (userId: string, newRole: 'admin' | 'gerente' | 'atendente') => {
    if (!organizationId) return;

    try {
      setActionLoading(true);
      await apiService.updateUserRole(organizationId, userId, newRole);
      setUsers(users.map(u => 
        u.usuarioId === userId ? { ...u, role: newRole } : u
      ));
    } catch (err: any) {
      console.error('Erro ao alterar role:', err);
      alert(err.response?.data?.message || 'Erro ao alterar função');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTogglePodeVerBacklog = async (userId: string, currentValue: boolean) => {
    try {
      setActionLoading(true);
      await apiService.updateUsuario(userId, { podeVerBacklog: !currentValue });
      setUsers(users.map(u => 
        u.usuarioId === userId ? { ...u, podeVerBacklog: !currentValue } : u
      ));
    } catch (err: any) {
      console.error('Erro ao alterar permissao backlog:', err);
      alert(err.response?.data?.message || 'Erro ao alterar permissão');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResendInvite = async (inviteId: string, email: string) => {
    if (!organizationId) return;

    try {
      setActionLoading(true);
      const result = await apiService.resendInvite(organizationId, inviteId);
      alert(`Convite reenviado para ${email}!\n\nLink: ${result.invite.inviteLink}`);
    } catch (err: any) {
      console.error('Erro ao reenviar convite:', err);
      alert(err.response?.data?.message || 'Erro ao reenviar convite');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organizationId || !organization) return;

    try {
      setActionLoading(true);
      const updated = await apiService.updateOrganization(organizationId, {
        name: editOrgName,
        timezone: editOrgTimezone,
        unsubscribeSettings: {
          enabled: editUnsubEnabled,
          baseUrl: editUnsubBaseUrl.trim(),
          footerTemplate: editUnsubFooterTemplate,
        },
      });
      setOrganization({ ...organization, ...updated });
      setShowEditOrgModal(false);
    } catch (err: any) {
      console.error('Erro ao atualizar organização:', err);
      alert(err.response?.data?.message || 'Erro ao atualizar organização');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpgradePlan = async () => {
    if (!selectedPlan || !organizationId || !organization) return;

    const targetPlan = plans.find(p => p.planId === selectedPlan);

    // If target plan is free, use direct plan change
    if (targetPlan?.priceMonthly === 0) {
      try {
        setActionLoading(true);
        const updated = await apiService.changePlan(organizationId, {
          planId: selectedPlan,
        });
        setOrganization({ ...organization, ...updated, planId: selectedPlan });

        // Update usage limits
        if (targetPlan && usage) {
          setUsage({
            ...usage,
            maxUsers: targetPlan.maxUsers,
            maxLeadsPerMonth: targetPlan.maxLeadsPerMonth,
            maxWhatsAppAccounts: targetPlan.maxWhatsAppAccounts,
          });
        }

        setShowUpgradeModal(false);
        setSelectedPlan(null);
      } catch (err: any) {
        console.error('Erro ao alterar plano:', err);
        alert(err.response?.data?.message || 'Erro ao alterar plano');
      } finally {
        setActionLoading(false);
      }
      return;
    }

    // For paid plans, redirect to Stripe Checkout
    try {
      setActionLoading(true);
      const { url } = await apiService.createCheckoutSession(selectedPlan, billingCycle);
      if (url) {
        window.location.href = url;
      } else {
        alert('Erro ao iniciar checkout');
      }
    } catch (err: any) {
      console.error('Erro ao iniciar checkout:', err);
      alert(err.response?.data?.message || 'Erro ao iniciar checkout');
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      setActionLoading(true);
      const { url } = await apiService.createCustomerPortalSession();
      if (url) {
        window.location.href = url;
      } else {
        alert('Erro ao abrir portal de pagamento');
      }
    } catch (err: any) {
      console.error('Erro ao abrir portal:', err);
      alert(err.response?.data?.message || 'Erro ao abrir portal de pagamento');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateDashboardGoals = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organizationId || !dashboardGoals) return;

    try {
      setActionLoading(true);
      const updatedGoals = await apiService.updateDashboardGoals(organizationId, dashboardGoals);
      setDashboardGoals(updatedGoals);
      alert('Metas do dashboard atualizadas com sucesso');
    } catch (err: any) {
      console.error('Erro ao atualizar metas:', err);
      alert(err.response?.data?.message || 'Erro ao atualizar metas');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription) return;
    if (!window.confirm('Tem certeza que deseja cancelar sua assinatura? Você continuará tendo acesso até o fim do período pago.')) {
      return;
    }

    try {
      setActionLoading(true);
      const canceledSubscription = await apiService.cancelSubscription();
      setSubscription(canceledSubscription);
      alert('Assinatura cancelada. Você continuará tendo acesso até ' +
        new Date(canceledSubscription.currentPeriodEnd).toLocaleDateString('pt-BR'));
    } catch (err: any) {
      console.error('Erro ao cancelar assinatura:', err);
      alert(err.response?.data?.message || 'Erro ao cancelar assinatura');
    } finally {
      setActionLoading(false);
    }
  };

  // WhatsApp Handlers
  const handleAddWhatsAppAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWhatsAppName.trim() || !newWhatsAppPhone.trim()) return;

    try {
      setActionLoading(true);
      const newAccount = await apiService.createWhatsAppAccount({
        nome: newWhatsAppName,
        telefone: newWhatsAppPhone.replace(/\D/g, ''),
      });
      setWhatsappAccounts([...whatsappAccounts, newAccount]);
      setNewWhatsAppName('');
      setNewWhatsAppPhone('');
      setShowAddWhatsAppModal(false);

      // Update usage
      if (usage) {
        setUsage({ ...usage, whatsappAccounts: usage.whatsappAccounts + 1 });
      }
    } catch (err: any) {
      console.error('Erro ao criar conta WhatsApp:', err);
      alert(err.response?.data?.message || 'Erro ao criar conta WhatsApp');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConnectWhatsApp = async (account: WhatsAppAccount) => {
    try {
      setActionLoading(true);
      setSelectedWhatsAppAccount(account);
      const response = await apiService.connectWhatsAppAccount(account.accountId);
      if (response.qrCode) {
        setQrCodeData(response.qrCode);
        setShowQRCodeModal(true);
      } else if (response.status === 'conectado') {
        // Update account status
        setWhatsappAccounts(whatsappAccounts.map(a =>
          a.accountId === account.accountId ? { ...a, status: 'conectado' as const } : a
        ));
        alert('Conta já está conectada!');
      }
    } catch (err: any) {
      console.error('Erro ao conectar WhatsApp:', err);
      alert(err.response?.data?.message || 'Erro ao conectar WhatsApp');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefreshWhatsAppStatus = async (account: WhatsAppAccount) => {
    try {
      const statusResponse = await apiService.getWhatsAppAccountStatus(account.accountId);
      setWhatsappAccounts(whatsappAccounts.map(a =>
        a.accountId === account.accountId
          ? { ...a, status: statusResponse.status as WhatsAppAccount['status'] }
          : a
      ));
    } catch (err: any) {
      console.error('Erro ao verificar status:', err);
    }
  };

  const handleOpenPendingQr = async (account: WhatsAppAccount) => {
    try {
      setActionLoading(true);
      setSelectedWhatsAppAccount(account);
      const statusResponse = await apiService.getWhatsAppAccountStatus(account.accountId);

      setWhatsappAccounts(whatsappAccounts.map(a =>
        a.accountId === account.accountId
          ? { ...a, status: statusResponse.status as WhatsAppAccount['status'] }
          : a
      ));

      if (statusResponse.status === 'aguardando_qr' && statusResponse.qrCode) {
        setQrCodeData(statusResponse.qrCode);
        setShowQRCodeModal(true);
        return;
      }

      if (statusResponse.status === 'aguardando_qr') {
        const connectResponse = await apiService.connectWhatsAppAccount(account.accountId);
        if (connectResponse.qrCode) {
          setQrCodeData(connectResponse.qrCode);
          setShowQRCodeModal(true);
          return;
        }
      }

      alert('Não há QR pendente para esta conta no momento. Tente reconectar novamente.');
    } catch (err: any) {
      console.error('Erro ao abrir QR pendente:', err);
      alert(err.response?.data?.message || 'Erro ao abrir QR pendente');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisconnectWhatsApp = async (account: WhatsAppAccount) => {
    if (!window.confirm('Tem certeza que deseja desconectar esta conta WhatsApp? Será necessário escanear o QR Code novamente para reconectar.')) return;
    try {
      setActionLoading(true);
      const updated = await apiService.disconnectWhatsAppAccount(account.accountId);
      setWhatsappAccounts(whatsappAccounts.map(a =>
        a.accountId === account.accountId ? { ...a, status: updated.status } : a
      ));
    } catch (err: any) {
      console.error('Erro ao desconectar WhatsApp:', err);
      alert(err.response?.data?.message || 'Erro ao desconectar WhatsApp');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelReconnectWhatsApp = async (account: WhatsAppAccount) => {
    if (!window.confirm('Cancelar reconexão desta conta e voltar para desconectado?')) return;
    try {
      setActionLoading(true);
      const updated = await apiService.disconnectWhatsAppAccount(account.accountId);
      setWhatsappAccounts(whatsappAccounts.map(a =>
        a.accountId === account.accountId ? { ...a, status: updated.status } : a
      ));
      if (selectedWhatsAppAccount?.accountId === account.accountId) {
        setShowQRCodeModal(false);
        setQrCodeData(null);
      }
    } catch (err: any) {
      console.error('Erro ao cancelar reconexão WhatsApp:', err);
      alert(err.response?.data?.message || 'Erro ao cancelar reconexão');
    } finally {
      setActionLoading(false);
    }
  };

  const userLinkedToWhatsAppAccount = (u: OrganizationUser, accountId: string) =>
    u.whatsappAccountId === accountId || u.whatsappAccountIdCampanha === accountId;

  const handleOpenAssignUsers = (account: WhatsAppAccount) => {
    setSelectedWhatsAppAccount(account);
    const assignedUsers = users.filter(
      u => !u.inviteStatus && userLinkedToWhatsAppAccount(u, account.accountId)
    );
    setSelectedUserIds(assignedUsers.map(u => u.usuarioId));
    setShowAssignUsersModal(true);
  };

  const handleAssignUsersToWhatsApp = async () => {
    if (!selectedWhatsAppAccount) return;

    try {
      setActionLoading(true);
      const accountId = selectedWhatsAppAccount.accountId;

      for (const u of users) {
        if (u.inviteStatus) continue;
        const inSelected = selectedUserIds.includes(u.usuarioId);
        const hasPrincipalHere = u.whatsappAccountId === accountId;
        const hasCampanhaOnlyHere =
          u.whatsappAccountIdCampanha === accountId && u.whatsappAccountId !== accountId;

        if (inSelected) {
          if (u.whatsappAccountId !== accountId) {
            await apiService.updateUsuarioWhatsApp(u.usuarioId, accountId);
          }
        } else {
          if (hasPrincipalHere) {
            await apiService.updateUsuarioWhatsApp(u.usuarioId, '');
          }
          if (hasCampanhaOnlyHere) {
            await apiService.updateUsuario(u.usuarioId, { whatsappAccountIdCampanha: null });
          }
        }
      }

      const usersData = await apiService.getOrganizationUsers(organizationId!);
      setUsers(usersData.users || []);
      setShowAssignUsersModal(false);
      setSelectedWhatsAppAccount(null);
      setSelectedUserIds([]);
    } catch (err: any) {
      console.error('Erro ao atribuir usuários:', err);
      alert(err.response?.data?.message || 'Erro ao atribuir usuários');
    } finally {
      setActionLoading(false);
    }
  };

  const canEditUserWhatsApp = (user: OrganizationUser) => {
    if (user.inviteStatus) return false;
    if (isOwner && user.usuarioId === usuario?.usuarioId) return true;
    if (isAdmin && user.role !== 'owner') return true;
    return false;
  };

  const openUserWhatsAppModal = (user: OrganizationUser) => {
    setUserWhatsAppEdit(user);
    setEditWaPrincipal(user.whatsappAccountId || '');
    setEditWaCampanha(user.whatsappAccountIdCampanha ?? '');
    setShowUserWhatsAppModal(true);
  };

  const handleSaveUserWhatsApp = async () => {
    if (!userWhatsAppEdit || !organizationId) return;
    const p = editWaPrincipal || null;
    const c = editWaCampanha || null;
    if (p && c && p === c) {
      alert('A conta principal e a de campanha devem ser diferentes.');
      return;
    }
    try {
      setActionLoading(true);
      await apiService.updateUsuario(userWhatsAppEdit.usuarioId, {
        whatsappAccountId: p,
        whatsappAccountIdCampanha: c,
      });
      const usersData = await apiService.getOrganizationUsers(organizationId);
      setUsers(usersData.users || []);
      if (userWhatsAppEdit.usuarioId === usuario?.usuarioId) {
        await refreshUsuario();
      }
      setShowUserWhatsAppModal(false);
      setUserWhatsAppEdit(null);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erro ao salvar contas WhatsApp');
    } finally {
      setActionLoading(false);
    }
  };

  const getWhatsAppStatusBadge = (status: WhatsAppAccount['status']) => {
    switch (status) {
      case 'conectado':
        return <span className="status-badge status-active">Conectado</span>;
      case 'desconectado':
        return <span className="status-badge status-inactive">Desconectado</span>;
      case 'aguardando_qr':
        return <span className="status-badge status-pending">Aguardando QR</span>;
      case 'erro':
        return <span className="status-badge status-error">Erro</span>;
      default:
        return <span className="status-badge">{status}</span>;
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'owner': return 'badge-owner';
      case 'admin': return 'badge-admin';
      case 'gerente': return 'badge-gerente';
      case 'atendente': return 'badge-atendente';
      default: return 'badge-user';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner': return 'Proprietário';
      case 'admin': return 'Administrador';
      case 'gerente': return 'Gerente';
      case 'atendente': return 'Atendente';
      default: return 'Usuário';
    }
  };

  const getUsagePercentage = (current: number, max: number) => {
    if (max < 0) return 0; // Ilimitado
    return Math.min((current / max) * 100, 100);
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'usage-critical';
    if (percentage >= 70) return 'usage-warning';
    return 'usage-normal';
  };

  const goDashboard = () => {
    if (embedded && onBackToDashboard) {
      onBackToDashboard();
      return;
    }
    navigate('/dashboard');
  };

  const goStatsDashboard = () => {
    if (embedded && onOpenStatsDashboard) {
      onOpenStatsDashboard();
      return;
    }
    navigate('/stats-dashboard');
  };

  if (loading) {
    return (
      <div className={`settings-page ${embedded ? 'embedded' : ''}`}>
        <div className="settings-loading">
          <div className="spinner"></div>
          <p>Carregando configurações...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`settings-page ${embedded ? 'embedded' : ''}`}>
        <div className="settings-error">
          <h2>Erro</h2>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={goDashboard}>
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`settings-page ${embedded ? 'embedded' : ''}`}>
      <div className="settings-container">
        {/* Header */}
        <header className="settings-header">
          <div className="settings-module-title">
            <h2>Configurações</h2>
            <p>Gestão de usuários, WhatsApp, plano e IA.</p>
          </div>
          <button className="settings-back-button" onClick={goDashboard}>
            Voltar ao kanban
          </button>
        </header>

        {/* Tabs */}
        <nav className="settings-tabs">
          <button 
            className={`tab ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            <span className="tab-icon">⚙️</span>
            Geral
          </button>
          <button
            className={`tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <span className="tab-icon">👥</span>
            Usuários
            <span className="tab-badge">{users.length}</span>
          </button>
          <button
            className={`tab ${activeTab === 'whatsapp' ? 'active' : ''}`}
            onClick={() => setActiveTab('whatsapp')}
          >
            <span className="tab-icon">📱</span>
            WhatsApp
            <span className="tab-badge">{whatsappAccounts.length}</span>
          </button>
          <button
            className={`tab ${activeTab === 'plan' ? 'active' : ''}`}
            onClick={() => setActiveTab('plan')}
          >
            <span className="tab-icon">💎</span>
            Plano
          </button>
          <button
            className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            <span className="tab-icon">📊</span>
            Dashboard
          </button>
          <button
            className={`tab ${activeTab === 'ai' ? 'active' : ''}`}
            onClick={() => setActiveTab('ai')}
          >
            <span className="tab-icon">✨</span>
            IA
          </button>
          <button
            className={`tab ${activeTab === 'kanban' ? 'active' : ''}`}
            onClick={() => setActiveTab('kanban')}
          >
            <span className="tab-icon">🗂️</span>
            Kanban
          </button>
        </nav>

        {/* Tab Content */}
        <div className="settings-content">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="tab-content">
              <section className="settings-section">
                <h2>Informações da Organização</h2>
                
                <div className="info-card">
                  <div className="info-row">
                    <span className="info-label">Nome</span>
                    <span className="info-value">{organization?.name}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Slug</span>
                    <span className="info-value">
                      <code>{organization?.slug}</code>
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Status</span>
                    <span className="info-value">
                      <span className={`status-badge status-${organization?.status}`}>
                        {organization?.status === 'active' ? 'Ativo' : organization?.status}
                      </span>
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Plano Atual</span>
                    <span className="info-value">
                      <span className="plan-badge">{currentPlan?.name}</span>
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Criado em</span>
                    <span className="info-value">
                      {organization && new Date(organization.createdAt).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Fuso horário</span>
                    <span className="info-value">{organization?.timezone || 'America/Sao_Paulo'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Rodapé de descadastro</span>
                    <span className="info-value">
                      {organization?.unsubscribeSettings?.enabled ?? true ? 'Ativado' : 'Desativado'}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">URL base unsubscribe</span>
                    <span className="info-value">
                      {organization?.unsubscribeSettings?.baseUrl || 'Padrão do ambiente'}
                    </span>
                  </div>
                  
                  {isOwner && (
                    <div className="info-actions">
                      <button 
                        className="btn btn-outline"
                        onClick={() => setShowEditOrgModal(true)}
                      >
                        Editar Informações
                      </button>
                    </div>
                  )}
                </div>
              </section>

              {(isOwner || isAdmin || isGerente) && (
                <section className="settings-section">
                  <h2>Campanhas e remarketing</h2>
                  <p style={{ color: '#64748b', fontSize: 13, marginBottom: 12 }}>
                    Quando um item de campanha originado em lost card passa a <strong>sucesso</strong> (resposta no
                    WhatsApp), o card volta ao funil com lembrete daqui a N meses.
                  </p>
                  <div className="info-card">
                    <div className="info-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                      <span className="info-label">Meses até o próximo contato (padrão)</span>
                      <input
                        type="number"
                        min={1}
                        max={120}
                        placeholder="Ex.: 3 — vazio desativa o automático"
                        value={campaignRemarketingMonths}
                        onChange={(e) => setCampaignRemarketingMonths(e.target.value)}
                        style={{ maxWidth: 280, padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0' }}
                      />
                    </div>
                    <div className="info-actions" style={{ marginTop: 12 }}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={campaignRemarketingSaving}
                        onClick={() => void handleSaveCampaignRemarketing()}
                      >
                        {campaignRemarketingSaving ? 'Salvando...' : 'Salvar'}
                      </button>
                    </div>
                  </div>
                </section>
              )}

              <section className="settings-section">
                <h2>Uso Atual</h2>
                
                <div className="usage-cards">
                  <div className="usage-card">
                    <div className="usage-header">
                      <span className="usage-icon">👥</span>
                      <span className="usage-title">Usuários</span>
                    </div>
                    <div className="usage-value">
                      <strong>{usage?.usersCount}</strong>
                      <span>/ {usage?.maxUsers}</span>
                    </div>
                    <div className="usage-bar">
                      <div 
                        className={`usage-fill ${getUsageColor(getUsagePercentage(usage?.usersCount || 0, usage?.maxUsers || 1))}`}
                        style={{ width: `${getUsagePercentage(usage?.usersCount || 0, usage?.maxUsers || 1)}%` }}
                      />
                    </div>
                  </div>

                  <div className="usage-card">
                    <div className="usage-header">
                      <span className="usage-icon">📊</span>
                      <span className="usage-title">Leads este mês</span>
                    </div>
                    <div className="usage-value">
                      <strong>{usage?.leadsThisMonth}</strong>
                      <span>/ {(usage?.maxLeadsPerMonth ?? 0) < 0 ? '∞' : usage?.maxLeadsPerMonth}</span>
                    </div>
                    <div className="usage-bar">
                      <div 
                        className={`usage-fill ${getUsageColor(getUsagePercentage(usage?.leadsThisMonth || 0, usage?.maxLeadsPerMonth || 1))}`}
                        style={{ width: `${getUsagePercentage(usage?.leadsThisMonth || 0, usage?.maxLeadsPerMonth || 1)}%` }}
                      />
                    </div>
                  </div>

                  <div className="usage-card">
                    <div className="usage-header">
                      <span className="usage-icon">📱</span>
                      <span className="usage-title">Contas WhatsApp</span>
                    </div>
                    <div className="usage-value">
                      <strong>{usage?.whatsappAccounts}</strong>
                      <span>/ {(usage?.maxWhatsAppAccounts ?? 0) < 0 ? '∞' : usage?.maxWhatsAppAccounts}</span>
                    </div>
                    <div className="usage-bar">
                      <div 
                        className={`usage-fill ${getUsageColor(getUsagePercentage(usage?.whatsappAccounts || 0, usage?.maxWhatsAppAccounts || 1))}`}
                        style={{ width: `${getUsagePercentage(usage?.whatsappAccounts || 0, usage?.maxWhatsAppAccounts || 1)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </section>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="tab-content">
              <section className="settings-section">
                <div className="section-header">
                  <div>
                    <h2>Membros da Equipe</h2>
                    <p className="section-subtitle">
                      {users.length} de {usage?.maxUsers} usuários
                    </p>
                  </div>
                  {isAdmin && (
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        // Check if at limit
                        if (usage?.maxUsers !== -1 && users.length >= (usage?.maxUsers || 0)) {
                          alert(`Você atingiu o limite de ${usage?.maxUsers} usuário(s) do plano ${currentPlan?.name || 'atual'}. Faça upgrade para adicionar mais usuários.`);
                          setActiveTab('plan');
                          return;
                        }
                        setShowInviteModal(true);
                      }}
                    >
                      + Convidar Usuário
                    </button>
                  )}
                </div>

                <div className="users-list">
                  {users.map(user => (
                    <div key={user.usuarioId} className="user-card">
                      <div className="user-avatar">
                        {user.nome.charAt(0).toUpperCase()}
                      </div>
                      <div className="user-info">
                        <div className="user-name">
                          {user.nome}
                          {user.inviteStatus === 'pending' && (
                            <span className="invite-badge">Pendente</span>
                          )}
                          {user.usuarioId === usuario?.usuarioId && (
                            <span className="you-badge">Você</span>
                          )}
                        </div>
                        <div className="user-email">{user.email}</div>
                      </div>
                      <div className="user-role">
                        <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                          {getRoleLabel(user.role)}
                        </span>
                        {canEditUserWhatsApp(user) && (
                          <button
                            type="button"
                            className="btn btn-outline btn-sm user-edit-btn"
                            onClick={() => openUserWhatsAppModal(user)}
                            disabled={actionLoading}
                            title="Editar contas WhatsApp (principal e campanha)"
                          >
                            Editar
                          </button>
                        )}
                      </div>
                      {isAdmin && user.role !== 'owner' && user.usuarioId !== usuario?.usuarioId && (
                        <div className="user-actions">
                            <>
                              {user.inviteStatus === 'pending' && (
                                <button
                                  className="btn btn-outline btn-sm"
                                  onClick={() => handleResendInvite((user.inviteId || user.usuarioId), user.email)}
                                  disabled={actionLoading}
                                  title="Reenviar convite"
                                >
                                  Reenviar
                                </button>
                              )}
                              {!user.inviteStatus && isOwner && (
                                <select
                                  value={user.role}
                                  onChange={(e) => handleChangeRole(user.usuarioId, e.target.value as 'admin' | 'gerente' | 'atendente')}
                                  className="role-select"
                                  disabled={actionLoading}
                                >
                                  <option value="atendente">Atendente</option>
                                  <option value="gerente">Gerente</option>
                                  <option value="admin">Administrador</option>
                                </select>
                              )}
                              {!user.inviteStatus && user.role === 'atendente' && (
                                <label className="backlog-toggle" title="Permitir ver backlog">
                                  <input
                                    type="checkbox"
                                    checked={(user as any).podeVerBacklog || false}
                                    onChange={() => handleTogglePodeVerBacklog(user.usuarioId, (user as any).podeVerBacklog || false)}
                                    disabled={actionLoading}
                                  />
                                  <span className="backlog-label">Ver Backlog</span>
                                </label>
                              )}
                              <button
                                className="btn-icon btn-danger"
                                onClick={() => handleRemoveUser(user.usuarioId)}
                                title="Remover usuário"
                                disabled={actionLoading}
                              >
                                🗑️
                              </button>
                            </>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}

          {/* WhatsApp Tab */}
          {activeTab === 'whatsapp' && (
            <div className="tab-content">
              <section className="settings-section">
                <div className="section-header">
                  <div>
                    <h2>Contas WhatsApp</h2>
                    <p className="section-subtitle">
                      {whatsappAccounts.length} de {(usage?.maxWhatsAppAccounts ?? 0) < 0 ? 'ilimitadas' : usage?.maxWhatsAppAccounts} contas
                    </p>
                  </div>
                  {isAdmin && (
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowAddWhatsAppModal(true)}
                      disabled={usage?.maxWhatsAppAccounts !== -1 && whatsappAccounts.length >= (usage?.maxWhatsAppAccounts || 0)}
                    >
                      + Adicionar Conta
                    </button>
                  )}
                </div>

                {whatsappAccounts.length === 0 ? (
                  <div className="empty-state">
                    <span className="empty-icon">📱</span>
                    <h3>Nenhuma conta WhatsApp</h3>
                    <p>Adicione uma conta para começar a receber leads via WhatsApp</p>
                    {isAdmin && (
                      <button
                        className="btn btn-primary"
                        onClick={() => setShowAddWhatsAppModal(true)}
                      >
                        Adicionar Primeira Conta
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="whatsapp-accounts-list">
                    {whatsappAccounts.map(account => {
                      const assignedUsers = users.filter(u =>
                        !u.inviteStatus && userLinkedToWhatsAppAccount(u, account.accountId)
                      );
                      return (
                        <div key={account.accountId} className="whatsapp-account-card">
                          <div className="whatsapp-account-header">
                            <div className="whatsapp-account-info">
                              <h3>{account.nome}</h3>
                              <p className="whatsapp-phone">{account.telefone}</p>
                            </div>
                            <div className="whatsapp-account-status">
                              {getWhatsAppStatusBadge(account.status)}
                            </div>
                          </div>

                          <div className="whatsapp-account-users">
                            <span className="users-label">Usuários atribuídos:</span>
                            {assignedUsers.length === 0 ? (
                              <span className="no-users">Nenhum usuário</span>
                            ) : (
                              <div className="assigned-users">
                                {assignedUsers.map(user => (
                                  <span key={user.usuarioId} className="assigned-user-badge">
                                    {user.nome}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>

                          {isAdmin && (
                            <div className="whatsapp-account-actions">
                              {(account.status === 'desconectado' || account.status === 'erro') && (
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={() => handleConnectWhatsApp(account)}
                                  disabled={actionLoading}
                                >
                                  {account.status === 'desconectado' ? 'Reconectar' : 'Conectar'}
                                </button>
                              )}
                              {account.status === 'aguardando_qr' && (
                                <>
                                  <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handleOpenPendingQr(account)}
                                    disabled={actionLoading}
                                  >
                                    Abrir QR
                                  </button>
                                  <button
                                    className="btn btn-outline btn-sm btn-danger-outline"
                                    onClick={() => handleCancelReconnectWhatsApp(account)}
                                    disabled={actionLoading}
                                  >
                                    Cancelar reconexão
                                  </button>
                                </>
                              )}
                              {account.status === 'conectado' && (
                                <>
                                  <button
                                    className="btn btn-outline btn-sm"
                                    onClick={() => handleRefreshWhatsAppStatus(account)}
                                    disabled={actionLoading}
                                  >
                                    Verificar Status
                                  </button>
                                  <button
                                    className="btn btn-outline btn-sm btn-danger-outline"
                                    onClick={() => handleDisconnectWhatsApp(account)}
                                    disabled={actionLoading}
                                    title="Desconectar a conta. Será necessário escanear o QR Code novamente."
                                  >
                                    Desconectar
                                  </button>
                                </>
                              )}
                              <button
                                className="btn btn-outline btn-sm"
                                onClick={() => handleOpenAssignUsers(account)}
                                disabled={actionLoading}
                              >
                                Atribuir Usuários
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>
          )}

          {/* Plan Tab */}
          {activeTab === 'plan' && (
            <div className="tab-content">
              {/* Trial Banner */}
              {organization?.trial?.isActive && (() => {
                const endDate = new Date(organization.trial!.endDate);
                const now = new Date();
                const daysLeft = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
                return (
                  <section className="settings-section" style={{ background: '#ECFDF5', border: '1px solid #A7F3D0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      <h2 style={{ margin: 0 }}>Trial Ativo</h2>
                      <span style={{
                        background: '#059669',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 600,
                        padding: '2px 10px',
                        borderRadius: '20px',
                      }}>
                        {daysLeft} dias restantes
                      </span>
                    </div>
                    <p style={{ color: '#065F46', fontSize: '14px', marginBottom: '12px' }}>
                      Expira em {endDate.toLocaleDateString('pt-BR')}
                    </p>
                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '12px' }}>
                      <div style={{ fontSize: '14px', color: '#065F46' }}>
                        <strong>{organization.trial!.maxUsers}</strong> usuarios
                      </div>
                      <div style={{ fontSize: '14px', color: '#065F46' }}>
                        <strong>{organization.trial!.maxLeadsPerMonth}</strong> leads/mes
                      </div>
                      <div style={{ fontSize: '14px', color: '#065F46' }}>
                        <strong>{organization.trial!.maxWhatsAppAccounts}</strong> contas WhatsApp
                      </div>
                    </div>
                    <p style={{ color: '#065F46', fontSize: '13px', fontStyle: 'italic' }}>
                      Apos o trial, os limites voltam ao plano Gratuito (1 usuario, 50 leads/mes, 1 conta WhatsApp).
                    </p>
                  </section>
                );
              })()}

              <section className="settings-section">
                <h2>Plano Atual</h2>

                {currentPlan && (
                  <div className="current-plan-card">
                    <div className="current-plan-header">
                      <div>
                        <h3>{currentPlan.name}</h3>
                        <p>{currentPlan.description}</p>
                      </div>
                      <div className="current-plan-price">
                        {currentPlan.planId === 'enterprise' ? (
                          <span className="price-free">Sob consulta</span>
                        ) : currentPlan.priceMonthly === 0 ? (
                          <span className="price-free">Grátis</span>
                        ) : (
                          <>
                            <span className="price-amount">R${currentPlan.priceMonthly}</span>
                            <span className="price-period">/mês</span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="current-plan-features">
                      <h4>Recursos incluídos:</h4>
                      <ul>
                        {currentPlan.features?.map((feature, index) => (
                          <li key={index}>✓ {feature}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Subscription Info */}
                    {subscription && (
                      <div className="subscription-info">
                        <h4>Detalhes da Assinatura</h4>
                        <div className="subscription-details">
                          <div className="subscription-row">
                            <span>Status:</span>
                            <span className={`subscription-status status-${subscription.status}`}>
                              {subscription.status === 'active' && 'Ativa'}
                              {subscription.status === 'past_due' && 'Pagamento Pendente'}
                              {subscription.status === 'canceled' && 'Cancelada'}
                              {subscription.status === 'trialing' && 'Período de Teste'}
                              {subscription.status === 'incomplete' && 'Incompleta'}
                            </span>
                          </div>
                          <div className="subscription-row">
                            <span>Ciclo:</span>
                            <span>{subscription.billingCycle === 'monthly' ? 'Mensal' : 'Anual'}</span>
                          </div>
                          <div className="subscription-row">
                            <span>Próxima cobrança:</span>
                            <span>{new Date(subscription.currentPeriodEnd).toLocaleDateString('pt-BR')}</span>
                          </div>
                          {subscription.cancelAtPeriodEnd && (
                            <div className="subscription-row">
                              <span className="cancel-notice">
                                Assinatura será cancelada em {new Date(subscription.currentPeriodEnd).toLocaleDateString('pt-BR')}
                              </span>
                            </div>
                          )}
                        </div>

                        {isOwner && (
                          <div className="subscription-actions">
                            <button
                              className="btn btn-outline"
                              onClick={handleManageBilling}
                              disabled={actionLoading}
                            >
                              Gerenciar Pagamento
                            </button>
                            {subscription.status === 'active' && !subscription.cancelAtPeriodEnd && (
                              <button
                                className="btn btn-ghost btn-danger-text"
                                onClick={handleCancelSubscription}
                                disabled={actionLoading}
                              >
                                Cancelar Assinatura
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </section>

              {isOwner && (
                <section className="settings-section">
                  <div className="section-header-with-toggle">
                    <div>
                      <h2>Alterar Plano</h2>
                      <p className="section-subtitle">
                        Compare os planos e escolha o melhor para sua equipe
                      </p>
                    </div>
                    <div className="billing-cycle-toggle">
                      <button
                        className={`cycle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
                        onClick={() => setBillingCycle('monthly')}
                      >
                        Mensal
                      </button>
                      <button
                        className={`cycle-btn ${billingCycle === 'yearly' ? 'active' : ''}`}
                        onClick={() => setBillingCycle('yearly')}
                      >
                        Anual
                        <span className="discount-badge">-20%</span>
                      </button>
                    </div>
                  </div>

                  <div className="plans-grid">
                    {plans.map(plan => {
                      const isCurrent = plan.planId === organization?.planId;
                      const isDowngrade = plans.findIndex(p => p.planId === plan.planId) < 
                                         plans.findIndex(p => p.planId === organization?.planId);
                      
                      return (
                        <div 
                          key={plan.planId} 
                          className={`plan-card ${isCurrent ? 'current' : ''} ${selectedPlan === plan.planId ? 'selected' : ''}`}
                          onClick={() => !isCurrent && setSelectedPlan(plan.planId)}
                        >
                          {isCurrent && <div className="plan-current-badge">Atual</div>}
                          
                          <h3>{plan.name}</h3>
                          <p className="plan-description">{plan.description}</p>
                          
                          <div className="plan-price">
                            {plan.planId === 'enterprise' ? (
                              <span className="price-free">Sob consulta</span>
                            ) : plan.priceMonthly === 0 ? (
                              <span className="price-free">Grátis</span>
                            ) : billingCycle === 'monthly' ? (
                              <>
                                <span className="price-currency">R$</span>
                                <span className="price-amount">{plan.priceMonthly}</span>
                                <span className="price-period">/mês</span>
                              </>
                            ) : (
                              <>
                                <span className="price-currency">R$</span>
                                <span className="price-amount">{Math.round(plan.priceYearly / 12)}</span>
                                <span className="price-period">/mês</span>
                                <span className="yearly-total">(R${plan.priceYearly}/ano)</span>
                              </>
                            )}
                          </div>
                          
                          <ul className="plan-limits">
                            {plan.planId === 'enterprise' ? (
                              <li>
                                <strong>Limites personalizados</strong>
                              </li>
                            ) : (
                              <>
                                <li>
                                  <strong>{plan.maxUsers}</strong> usuários
                                </li>
                                <li>
                                  <strong>{plan.maxLeadsPerMonth}</strong> leads/mês
                                </li>
                                <li>
                                  <strong>{plan.maxWhatsAppAccounts}</strong> contas WhatsApp
                                </li>
                              </>
                            )}
                          </ul>
                          
                          {!isCurrent && (
                            plan.planId === 'enterprise' ? (
                              <a 
                                href="mailto:contato@ghiconnect.com.br?subject=Interesse no plano Enterprise"
                                className="btn btn-outline btn-block"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Fale Conosco
                              </a>
                            ) : (
                              <button 
                                className={`btn ${isDowngrade ? 'btn-outline' : 'btn-primary'} btn-block`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedPlan(plan.planId);
                                  setShowUpgradeModal(true);
                                }}
                              >
                                {isDowngrade ? 'Fazer Downgrade' : 'Fazer Upgrade'}
                              </button>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            </div>
          )}

          {/* Dashboard Goals Tab */}
          {activeTab === 'dashboard' && (
            <div className="tab-content">
              <section className="settings-section">
                <div className="section-header">
                  <div>
                    <h2>Metas do Dashboard</h2>
                    <p className="section-subtitle">
                      Defina metas operacionais de leads e atendimento
                    </p>
                  </div>
                  <button className="btn btn-outline" onClick={goStatsDashboard}>
                    Ver Dashboard
                  </button>
                </div>

                {!dashboardGoals ? (
                  <div className="info-card">
                    <p>Não foi possível carregar metas no momento.</p>
                  </div>
                ) : (
                  <form className="dashboard-goals-form" onSubmit={handleUpdateDashboardGoals}>
                    <div className="dashboard-goals-grid">
                      <div className="form-group">
                        <label>Meta leads recebidos (mês)</label>
                        <input
                          type="number"
                          min={0}
                          value={dashboardGoals.leadsReceivedMonth}
                          onChange={(e) => setDashboardGoals({ ...dashboardGoals, leadsReceivedMonth: Number(e.target.value) })}
                          disabled={!isAdmin || actionLoading}
                        />
                      </div>
                      <div className="form-group">
                        <label>Meta leads atendidos (mês)</label>
                        <input
                          type="number"
                          min={0}
                          value={dashboardGoals.leadsAttendedMonth}
                          onChange={(e) => setDashboardGoals({ ...dashboardGoals, leadsAttendedMonth: Number(e.target.value) })}
                          disabled={!isAdmin || actionLoading}
                        />
                      </div>
                      <div className="form-group">
                        <label>Meta leads convertidos (mês)</label>
                        <input
                          type="number"
                          min={0}
                          value={dashboardGoals.leadsConvertedMonth}
                          onChange={(e) => setDashboardGoals({ ...dashboardGoals, leadsConvertedMonth: Number(e.target.value) })}
                          disabled={!isAdmin || actionLoading}
                        />
                      </div>
                      <div className="form-group">
                        <label>Meta conversão (%)</label>
                        <input
                          type="number"
                          min={0}
                          step={0.1}
                          value={dashboardGoals.conversionRatePercent}
                          onChange={(e) => setDashboardGoals({ ...dashboardGoals, conversionRatePercent: Number(e.target.value) })}
                          disabled={!isAdmin || actionLoading}
                        />
                      </div>
                      <div className="form-group">
                        <label>Meta tempo 1ª resposta (min)</label>
                        <input
                          type="number"
                          min={0}
                          step={0.1}
                          value={dashboardGoals.firstResponseTimeMinutes}
                          onChange={(e) => setDashboardGoals({ ...dashboardGoals, firstResponseTimeMinutes: Number(e.target.value) })}
                          disabled={!isAdmin || actionLoading}
                        />
                      </div>
                      <div className="form-group">
                        <label>Meta backlog máximo</label>
                        <input
                          type="number"
                          min={0}
                          value={dashboardGoals.maxBacklog}
                          onChange={(e) => setDashboardGoals({ ...dashboardGoals, maxBacklog: Number(e.target.value) })}
                          disabled={!isAdmin || actionLoading}
                        />
                      </div>
                    </div>

                    {isAdmin ? (
                      <div className="info-actions">
                        <button type="submit" className="btn btn-primary" disabled={actionLoading}>
                          {actionLoading ? 'Salvando...' : 'Salvar metas'}
                        </button>
                      </div>
                    ) : (
                      <p className="section-subtitle">Somente administradores podem alterar as metas.</p>
                    )}
                  </form>
                )}
              </section>
            </div>
          )}

          {/* AI Tab */}
          {activeTab === 'ai' && organizationId && (
            <div className="tab-content">
              <AISettingsTab
                isOwner={isOwner}
                isAdmin={isAdmin}
                isGerente={isGerente}
                organizationId={organizationId}
              />
            </div>
          )}

          {/* Kanban Tab */}
          {activeTab === 'kanban' && organizationId && (
            <div className="tab-content">
              <KanbanSettingsTab isOwner={isOwner} isAdmin={isAdmin} organizationId={organizationId} />
            </div>
          )}
        </div>
      </div>

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Convidar Usuário</h3>
              <button className="modal-close" onClick={() => setShowInviteModal(false)}>×</button>
            </div>
            <form onSubmit={handleInviteUser}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    placeholder="email@exemplo.com"
                    required
                    disabled={actionLoading}
                  />
                </div>
                <div className="form-group">
                  <label>Função</label>
                  <select 
                    value={inviteRole}
                    onChange={e => setInviteRole(e.target.value as 'admin' | 'gerente' | 'atendente')}
                    disabled={actionLoading}
                  >
                    <option value="atendente">Atendente</option>
                    <option value="gerente">Gerente</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
                <p className="modal-hint">
                  Um email será enviado com o convite para ingressar na organização.
                </p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-ghost" 
                  onClick={() => setShowInviteModal(false)}
                  disabled={actionLoading}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={actionLoading}>
                  {actionLoading ? 'Enviando...' : 'Enviar Convite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Organization Modal */}
      {showEditOrgModal && (
        <div className="modal-overlay" onClick={() => setShowEditOrgModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Editar Organização</h3>
              <button className="modal-close" onClick={() => setShowEditOrgModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateOrganization}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nome da Organização</label>
                  <input
                    type="text"
                    value={editOrgName}
                    onChange={e => setEditOrgName(e.target.value)}
                    required
                    disabled={actionLoading}
                  />
                </div>
                <div className="form-group">
                  <label>Fuso horário da empresa</label>
                  <select
                    value={editOrgTimezone}
                    onChange={e => setEditOrgTimezone(e.target.value)}
                    disabled={actionLoading}
                  >
                    {TIMEZONE_OPTIONS.map((timezoneOption) => (
                      <option key={timezoneOption} value={timezoneOption}>
                        {timezoneOption}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={editUnsubEnabled}
                      onChange={(e) => setEditUnsubEnabled(e.target.checked)}
                      disabled={actionLoading}
                    />{' '}
                    Inserir rodapé de descadastro em mensagens de campanha
                  </label>
                </div>
                <div className="form-group">
                  <label>URL base para unsubscribe (opcional)</label>
                  <input
                    type="text"
                    value={editUnsubBaseUrl}
                    onChange={(e) => setEditUnsubBaseUrl(e.target.value)}
                    placeholder="https://ghiconnect.com.br"
                    disabled={actionLoading}
                  />
                </div>
                <div className="form-group">
                  <label>Template do rodapé</label>
                  <textarea
                    value={editUnsubFooterTemplate}
                    onChange={(e) => setEditUnsubFooterTemplate(e.target.value)}
                    rows={4}
                    disabled={actionLoading}
                    placeholder="Texto do rodapé"
                  />
                  <small>
                    Use <code>{'{unsubscribeUrl}'}</code> para inserir o link (a URL base é o campo acima). No
                    WhatsApp não dá para esconder a URL atrás de um texto; a URL precisa aparecer para ficar
                    clicável.
                  </small>
                </div>
                <div className="form-group">
                  <label>Preview do rodapé</label>
                  <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                    {(editUnsubFooterTemplate || '').includes('{unsubscribeUrl}')
                      ? editUnsubFooterTemplate.replace(
                          /\{unsubscribeUrl\}/g,
                          `${editUnsubBaseUrl.trim() || 'https://ghiconnect.com.br'}/u/CODE`
                        )
                      : `${editUnsubFooterTemplate}\n${
                          editUnsubBaseUrl.trim() || 'https://ghiconnect.com.br'
                        }/u/CODE`}
                  </pre>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-ghost" 
                  onClick={() => setShowEditOrgModal(false)}
                  disabled={actionLoading}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={actionLoading}>
                  {actionLoading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upgrade Plan Modal */}
      {showUpgradeModal && selectedPlan && (
        <div className="modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirmar Alteração de Plano</h3>
              <button className="modal-close" onClick={() => setShowUpgradeModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="plan-change-summary">
                <div className="plan-change-from">
                  <span className="plan-change-label">Plano Atual</span>
                  <strong>{currentPlan?.name}</strong>
                  <span>{currentPlan?.planId === 'enterprise' ? 'Sob consulta' : currentPlan?.priceMonthly === 0 ? 'Grátis' : `R$${currentPlan?.priceMonthly}/mês`}</span>
                </div>
                <div className="plan-change-arrow">→</div>
                <div className="plan-change-to">
                  <span className="plan-change-label">Novo Plano</span>
                  <strong>{plans.find(p => p.planId === selectedPlan)?.name}</strong>
                  {(() => {
                    const targetPlan = plans.find(p => p.planId === selectedPlan);
                    if (!targetPlan) return null;
                    if (targetPlan.planId === 'enterprise') {
                      return <span>Sob consulta</span>;
                    }
                    if (targetPlan.priceMonthly === 0) {
                      return <span>Grátis</span>;
                    }
                    return billingCycle === 'monthly' ? (
                      <span>R${targetPlan.priceMonthly}/mês</span>
                    ) : (
                      <span>R${targetPlan.priceYearly}/ano</span>
                    );
                  })()}
                </div>
              </div>
              {(() => {
                const targetPlan = plans.find(p => p.planId === selectedPlan);
                if (targetPlan && targetPlan.priceMonthly > 0) {
                  return (
                    <p className="modal-hint">
                      Você será redirecionado para o checkout seguro do Stripe para completar o pagamento.
                      {billingCycle === 'yearly' && ' Com o plano anual você economiza 20%!'}
                    </p>
                  );
                }
                return (
                  <p className="modal-hint">
                    A alteração será aplicada imediatamente.
                  </p>
                );
              })()}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowUpgradeModal(false)}
                disabled={actionLoading}
              >
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpgradePlan}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processando...' : (
                  plans.find(p => p.planId === selectedPlan)?.priceMonthly === 0
                    ? 'Confirmar Alteração'
                    : 'Ir para Pagamento'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add WhatsApp Account Modal */}
      {showAddWhatsAppModal && (
        <div className="modal-overlay" onClick={() => setShowAddWhatsAppModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Adicionar Conta WhatsApp</h3>
              <button className="modal-close" onClick={() => setShowAddWhatsAppModal(false)}>×</button>
            </div>
            <form onSubmit={handleAddWhatsAppAccount}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nome da Conta</label>
                  <input
                    type="text"
                    value={newWhatsAppName}
                    onChange={e => setNewWhatsAppName(e.target.value)}
                    placeholder="Ex: Vendas, Suporte, etc."
                    required
                    disabled={actionLoading}
                  />
                </div>
                <div className="form-group">
                  <label>Número do WhatsApp</label>
                  <input
                    type="tel"
                    value={newWhatsAppPhone}
                    onChange={e => setNewWhatsAppPhone(e.target.value)}
                    placeholder="+55 11 99999-9999"
                    required
                    disabled={actionLoading}
                  />
                </div>
                <p className="modal-hint">
                  Após criar a conta, você precisará escanear o QR Code para conectar.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setShowAddWhatsAppModal(false)}
                  disabled={actionLoading}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={actionLoading}>
                  {actionLoading ? 'Criando...' : 'Criar Conta'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRCodeModal && qrCodeData && (
        <div className="modal-overlay" onClick={() => {
          setShowQRCodeModal(false);
          if (selectedWhatsAppAccount) {
            handleRefreshWhatsAppStatus(selectedWhatsAppAccount);
          }
        }}>
          <div className="modal modal-qrcode" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Conectar WhatsApp</h3>
              <button className="modal-close" onClick={() => {
                setShowQRCodeModal(false);
                if (selectedWhatsAppAccount) {
                  handleRefreshWhatsAppStatus(selectedWhatsAppAccount);
                }
              }}>×</button>
            </div>
            <div className="modal-body">
              <div className="qrcode-container">
                <img src={qrCodeData} alt="QR Code WhatsApp" className="qrcode-image" />
              </div>
              <div className="qrcode-instructions">
                <h4>Como conectar:</h4>
                <ol>
                  <li>Abra o WhatsApp no seu celular</li>
                  <li>Toque em Menu ou Configurações</li>
                  <li>Selecione "Aparelhos conectados"</li>
                  <li>Toque em "Conectar aparelho"</li>
                  <li>Aponte a câmera para este QR Code</li>
                </ol>
              </div>
              <p className="modal-hint">
                O QR Code expira em alguns minutos. Se não funcionar, feche e tente novamente.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowQRCodeModal(false);
                  setQrCodeData(null);
                  if (selectedWhatsAppAccount) {
                    handleRefreshWhatsAppStatus(selectedWhatsAppAccount);
                  }
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Editar usuário — contas WhatsApp principal e campanha */}
      {showUserWhatsAppModal && userWhatsAppEdit && (
        <div className="modal-overlay" onClick={() => !actionLoading && setShowUserWhatsAppModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Editar usuário — {userWhatsAppEdit.nome}</h3>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowUserWhatsAppModal(false)}
                disabled={actionLoading}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-subtitle">
                Conta <strong>principal</strong> é usada para atendimento e transferência de leads. Conta{' '}
                <strong>campanha</strong> é opcional para disparos de campanha. Se ambas forem preenchidas, devem ser
                contas diferentes.
              </p>
              <div className="form-group">
                <label htmlFor="wa-principal">Conta principal</label>
                <select
                  id="wa-principal"
                  className="form-select"
                  value={editWaPrincipal}
                  onChange={e => setEditWaPrincipal(e.target.value)}
                  disabled={actionLoading}
                >
                  <option value="">Nenhuma</option>
                  {whatsappAccounts.map(a => (
                    <option key={a.accountId} value={a.accountId}>
                      {a.nome} — {a.telefone}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="wa-campanha">Conta campanha</label>
                <select
                  id="wa-campanha"
                  className="form-select"
                  value={editWaCampanha}
                  onChange={e => setEditWaCampanha(e.target.value)}
                  disabled={actionLoading}
                >
                  <option value="">Nenhuma</option>
                  {whatsappAccounts.map(a => (
                    <option key={a.accountId} value={a.accountId}>
                      {a.nome} — {a.telefone}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowUserWhatsAppModal(false)}
                disabled={actionLoading}
              >
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSaveUserWhatsApp} disabled={actionLoading}>
                {actionLoading ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Users to WhatsApp Modal */}
      {showAssignUsersModal && selectedWhatsAppAccount && (
        <div className="modal-overlay" onClick={() => setShowAssignUsersModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Atribuir Usuários</h3>
              <button className="modal-close" onClick={() => setShowAssignUsersModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-subtitle">
                Selecione os usuários que podem usar a conta <strong>{selectedWhatsAppAccount.nome}</strong> como{' '}
                <strong>conta principal</strong> (atendimento). Para campanha, use a edição do usuário na aba Membros.
              </p>
              <div className="users-checkbox-list">
                {users.filter(u => !u.inviteStatus).map(user => (
                  <label key={user.usuarioId} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(user.usuarioId)}
                      onChange={e => {
                        if (e.target.checked) {
                          setSelectedUserIds([...selectedUserIds, user.usuarioId]);
                        } else {
                          setSelectedUserIds(selectedUserIds.filter(id => id !== user.usuarioId));
                        }
                      }}
                      disabled={actionLoading}
                    />
                    <span className="checkbox-label">
                      <span className="user-name">{user.nome}</span>
                      <span className="user-email">{user.email}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowAssignUsersModal(false)}
                disabled={actionLoading}
              >
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAssignUsersToWhatsApp}
                disabled={actionLoading}
              >
                {actionLoading ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
